import type { AxiosError } from 'axios'
import { EyeOff, FileSpreadsheet, Network, Plus, Settings2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import {
	useCreateSystemBranch,
	useDeleteSystemBranch,
	useUpdateBranchCustomName,
	useUpdateBranchVisibility,
	useUpdateCROverrides,
} from '@/api/queries/organization-overrides-queries'
import { useOrganization } from '@/api/queries/organization-queries'
import {
	CsvImportTab,
	OrgTree,
	SystemBranchFormContent,
} from '@/components/administration'
import type { SystemBranchFormData } from '@/components/administration/system-branch-form'
import {
	AdminCardPage,
	ResponsiveFormModal,
	SearchInput,
} from '@/components/commons'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { adminBackRoute } from '@/constants/admin-menu'
import { ROUTES } from '@/constants/routes'
import { cn } from '@/lib/utils'

type ViewFilter = 'all' | 'erp' | 'virtual'

interface BackendErrorResponse {
	error_code?: string
	message?: string
	data?: unknown
}

const normalizeDataToKey = (value: string): string =>
	value
		.trim()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toUpperCase()
		.replace(/[^A-Z0-9]+/g, '_')
		.replace(/^_+|_+$/g, '')

export const OrgOverridesPage = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()

	const [search, setSearch] = useState('')
	const [formOpen, setFormOpen] = useState(false)
	const [viewFilter, setViewFilter] = useState<ViewFilter>('all')

	/* ── Data ── */
	const { data: organization, isLoading } = useOrganization(true)
	const createBranchMutation = useCreateSystemBranch()
	const deleteBranchMutation = useDeleteSystemBranch()
	const renameBranchMutation = useUpdateBranchCustomName()
	const visibilityMutation = useUpdateBranchVisibility()
	const updateCRMutation = useUpdateCROverrides()

	const branches = organization ?? []

	const erpCount = branches.filter(
		(b) => !(b.code ?? '').startsWith('SYS-'),
	).length
	const virtualCount = branches.filter((b) =>
		(b.code ?? '').startsWith('SYS-'),
	).length
	const hiddenCount = branches.filter((b) => b.isHidden).length

	const getBackendErrorMessage = (
		error: unknown,
		fallbackKey: string,
	): string => {
		const responseData = (error as AxiosError).response?.data as
			| BackendErrorResponse
			| undefined
		const errorCode = responseData?.error_code?.toUpperCase()

		if (errorCode) {
			const rawData = responseData?.data
			if (typeof rawData === 'string' && rawData.trim()) {
				const dataKey = normalizeDataToKey(rawData)
				if (dataKey) {
					const specificKey = `administration.orgOverrides.backendErrors.${errorCode}.${dataKey}`
					const specificTranslation = t(specificKey)
					if (specificTranslation !== specificKey) return specificTranslation
				}
			}

			const defaultKey = `administration.orgOverrides.backendErrors.${errorCode}._default`
			const defaultTranslation = t(defaultKey)
			if (defaultTranslation !== defaultKey) return defaultTranslation
		}

		if (typeof responseData?.data === 'string' && responseData.data.trim()) {
			return responseData.data
		}

		if (
			typeof responseData?.message === 'string' &&
			responseData.message.trim()
		) {
			return responseData.message
		}

		return t(fallbackKey)
	}

	/* ── Filtering + sorting ── */
	const filtered = useMemo(() => {
		let result = [...branches]

		if (viewFilter === 'virtual')
			result = result.filter((b) => (b.code ?? '').startsWith('SYS-'))
		else if (viewFilter === 'erp')
			result = result.filter((b) => !(b.code ?? '').startsWith('SYS-'))

		if (search.trim()) {
			const q = search.toLowerCase()
			result = result.filter(
				(b) =>
					b.name.toLowerCase().includes(q) ||
					(b.code ?? '').toLowerCase().includes(q) ||
					b.crs.some(
						(cr) =>
							cr.name.toLowerCase().includes(q) ||
							(cr.code ?? '').toLowerCase().includes(q),
					),
			)
		}

		return result.sort((a, b) => {
			const aEmpty = a.crs.length === 0
			const bEmpty = b.crs.length === 0
			if (aEmpty !== bEmpty) return aEmpty ? 1 : -1
			return a.name.localeCompare(b.name)
		})
	}, [branches, search, viewFilter])

	/* ── Handlers ── */
	const handleCreateBranch = async (data: SystemBranchFormData) => {
		try {
			await createBranchMutation.mutateAsync({
				name: data.name,
				code: data.code,
				customName: data.customName || undefined,
			})
			toast.success(
				t('administration.orgOverrides.createBranchSuccess', {
					name: data.customName || data.name,
				}),
			)
			setFormOpen(false)
		} catch (e) {
			toast.error(
				getBackendErrorMessage(e, 'administration.orgOverrides.saveError'),
			)
		}
	}

	const handleRenameBranch = async (
		branchId: string,
		newName: string | null,
	) => {
		try {
			await renameBranchMutation.mutateAsync({
				id: branchId,
				body: { customName: newName },
			})
			toast.success(
				newName
					? t('administration.orgOverrides.renameBranchSuccess')
					: t('administration.orgOverrides.resetNameSuccess'),
			)
		} catch (e) {
			toast.error(
				getBackendErrorMessage(e, 'administration.orgOverrides.saveError'),
			)
		}
	}

	const handleRenameCR = async (crId: string, newName: string | null) => {
		try {
			await updateCRMutation.mutateAsync({
				id: crId,
				body: { customName: newName },
			})
			toast.success(
				newName
					? t('administration.orgOverrides.renameCRSuccess')
					: t('administration.orgOverrides.resetNameSuccess'),
			)
		} catch (e) {
			toast.error(
				getBackendErrorMessage(e, 'administration.orgOverrides.saveError'),
			)
		}
	}

	const handleMoveCR = async (crId: string, targetBranchId: string | null) => {
		try {
			await updateCRMutation.mutateAsync({
				id: crId,
				body: { systemBranchId: targetBranchId },
			})
			toast.success(
				targetBranchId
					? t('administration.orgOverrides.moveCRSuccess')
					: t('administration.orgOverrides.undoMoveSuccess'),
			)
		} catch (e) {
			toast.error(
				getBackendErrorMessage(e, 'administration.orgOverrides.saveError'),
			)
		}
	}

	const handleToggleVisibility = async (
		branchId: string,
		isHidden: boolean,
	) => {
		try {
			await visibilityMutation.mutateAsync({ id: branchId, body: { isHidden } })
			toast.success(
				isHidden
					? t('administration.orgOverrides.hideBranchSuccess')
					: t('administration.orgOverrides.showBranchSuccess'),
			)
		} catch (e) {
			toast.error(
				getBackendErrorMessage(e, 'administration.orgOverrides.saveError'),
			)
		}
	}

	const handleDeleteBranch = async (branchId: string) => {
		try {
			await deleteBranchMutation.mutateAsync({ id: branchId })
			toast.success(t('administration.orgOverrides.deleteBranchSuccess'))
		} catch (e) {
			toast.error(
				getBackendErrorMessage(
					e,
					'administration.orgOverrides.deleteBranchError',
				),
			)
		}
	}

	return (
		<AdminCardPage
			icon={<Network className="size-5" />}
			title={t('administration.orgOverrides.title')}
			subtitle={t('administration.orgOverrides.subtitle')}
			onBack={() =>
				navigate(adminBackRoute(ROUTES.ADMINISTRATION.ORG_OVERRIDES))
			}
			footerText={`${branches.length} ${
				branches.length === 1
					? t('administration.orgOverrides.countSingular')
					: t('administration.orgOverrides.countPlural')
			}`}
			actions={
				<Button
					onClick={() => setFormOpen(true)}
					className="gap-2 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90 w-full sm:w-auto"
				>
					<Plus className="size-4" />
					<span>{t('administration.orgOverrides.createVirtualBranch')}</span>
				</Button>
			}
			modal={
				<ResponsiveFormModal
					open={formOpen}
					onOpenChange={setFormOpen}
					title={t('administration.orgOverrides.form.title')}
					dialogClassName="sm:max-w-md"
				>
					<SystemBranchFormContent
						onSave={handleCreateBranch}
						onClose={() => setFormOpen(false)}
					/>
				</ResponsiveFormModal>
			}
		>
			<Tabs defaultValue="manual">
				<TabsList>
					<TabsTrigger value="manual" className="gap-1.5">
						<Settings2 className="size-3.5" />
						{t('administration.orgOverrides.tabManual')}
					</TabsTrigger>
					<TabsTrigger value="csv" className="gap-1.5">
						<FileSpreadsheet className="size-3.5" />
						{t('administration.orgOverrides.tabCsv')}
					</TabsTrigger>
				</TabsList>

				<TabsContent value="manual" className="space-y-3 ">
					{/* Badge filter chips */}
					<div className="flex items-center justify-between gap-2 flex-wrap">
						<div className="flex items-center gap-1.5 flex-wrap">
							{(
								[
									{
										key: 'all' as const,
										label: t('administration.orgOverrides.viewAll', {
											count: branches.length,
										}),
									},
									{
										key: 'erp' as const,
										label: t('administration.orgOverrides.viewErp', {
											count: erpCount,
										}),
									},
									{
										key: 'virtual' as const,
										label: t('administration.orgOverrides.viewVirtual', {
											count: virtualCount,
										}),
									},
								] as const
							).map(({ key, label }) => (
								<Badge
									key={key}
									asChild
									variant={viewFilter === key ? 'default' : 'outline'}
									className={cn(
										'cursor-pointer select-none transition-all',
										viewFilter === key
											? 'bg-kaefer-dark-grey text-white hover:bg-kaefer-dark-grey/90'
											: 'hover:bg-muted hover:text-kaefer-dark-grey',
									)}
								>
									<button type="button" onClick={() => setViewFilter(key)}>
										{label}
									</button>
								</Badge>
							))}
						</div>
						{hiddenCount > 0 && (
							<span className="flex items-center gap-1.5 text-xs text-muted-foreground">
								<EyeOff className="size-3 shrink-0" />
								{t('administration.orgOverrides.hiddenInfo', {
									count: hiddenCount,
								})}
							</span>
						)}
					</div>

					<SearchInput
						value={search}
						onChange={setSearch}
						placeholder={t('administration.orgOverrides.searchPlaceholder')}
					/>
					{isLoading ? (
						<div className="flex items-center justify-center py-16">
							<span className="text-sm text-muted-foreground">
								{t('administration.orgOverrides.loading')}
							</span>
						</div>
					) : (
						<OrgTree
							branches={filtered}
							allBranches={branches}
							onRenameBranch={handleRenameBranch}
							onDeleteBranch={handleDeleteBranch}
							onToggleVisibility={handleToggleVisibility}
							onRenameCR={handleRenameCR}
							onMoveCR={handleMoveCR}
						/>
					)}
				</TabsContent>

				<TabsContent value="csv">
					<CsvImportTab branches={branches} />
				</TabsContent>
			</Tabs>
		</AdminCardPage>
	)
}
